import { saveAs } from "file-saver";
import Toast from "@moltech/molui/toast";
import { pick } from "./copilotUtils";

const normalizeApiPath = (url) => {
  const raw = String(url || "").trim();
  if (!raw) return "";
  return raw.startsWith("/") ? raw.slice(1) : raw;
};

const toLocalYmd = (value) => {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value.trim())) {
    return value.trim().slice(0, 10);
  }
  const date = value instanceof Date ? value : new Date(value || Date.now());
  if (Number.isNaN(date.getTime())) {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const resolveFileName = (payload, blob) => {
  const explicit = pick(payload, "fileName", "FileName");
  if (explicit) return explicit;
  const fromBlob = blob?.filename || blob?.name;
  if (fromBlob) return fromBlob;
  return "MolAssist_Export.xlsx";
};

const asDownloadBlob = (res) => {
  if (!res) return null;
  if (res instanceof Blob) return res;
  // RTK sometimes returns the mutation envelope when unwrap is skipped.
  if (res.data instanceof Blob) return res.data;
  if (res.data != null) return new Blob([res.data]);
  return new Blob([res]);
};

const looksLikeJsonErrorBlob = async (blob) => {
  if (!(blob instanceof Blob) || blob.size === 0) return false;
  const type = String(blob.type || "").toLowerCase();
  if (type.includes("json") || type.includes("text")) return true;
  // Small bodies from BadRequest are usually JSON text, not real xlsx.
  if (blob.size > 4096) return false;
  try {
    const text = await blob.slice(0, 64).text();
    const trimmed = text.trim();
    return trimmed.startsWith("{") || trimmed.startsWith("[");
  } catch {
    return false;
  }
};

/**
 * Download a registered MolAssist export via existing Report / ManageApplicants file endpoints.
 * Caller must pass a POST helper that uses responseHandler: response.blob().
 */
export const downloadCopilotExport = async (action, { postFileData, getFileData }) => {
  const payload = pick(action, "payload", "Payload") || {};
  const url = normalizeApiPath(pick(action, "path", "Path") || pick(payload, "url", "Url"));
  if (!url) {
    Toast.warn("Export URL is missing.");
    return false;
  }

  const method = String(pick(payload, "method", "Method") || "POST").toUpperCase();
  const body = pick(payload, "body", "Body") || {};
  try {
    let res;
    if (method === "GET") {
      res = await getFileData({ url, data: body }).unwrap();
    } else {
      res = await postFileData({ url, data: body }).unwrap();
    }

    const blob = asDownloadBlob(res);
    if (!blob || blob.size === 0) {
      Toast.error("Export returned no file.");
      return false;
    }
    if (await looksLikeJsonErrorBlob(blob)) {
      Toast.error("Export download failed. Try again or open the report page.");
      return false;
    }

    saveAs(blob, resolveFileName(payload, blob));
    Toast.success("Download started.");
    return true;
  } catch (err) {
    Toast.error(err?.data?.message || err?.message || "Export download failed.");
    return false;
  }
};

export const buildDailyReportExportAction = (reportDate) => {
  const day = toLocalYmd(reportDate);
  const [y, m, d] = day.split("-");
  const labelDate = d && m && y ? `${d}-${m}-${y}` : day;
  return {
    type: "download",
    label: "Download Excel",
    payload: {
      exportCode: "daily_recruitment_report",
      url: "/Report/ExportDailyRecruitmentReport",
      method: "POST",
      body: { reportDate: day },
      fileName: `Recruitment_Report_${labelDate}.xlsx`,
      format: "xlsx",
    },
  };
};
